import React, { useEffect, useRef, useState } from 'react';

const Services = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeService, setActiveService] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const services = [
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
      title: 'Web Development',
      shortDesc: 'Modern, responsive web applications',
      fullDesc: 'Scalizap builds modern, responsive web applications featuring intuitive interfaces and seamless performance across all devices. We combine sleek design with robust functionality to drive user engagement and accelerate your growth.',
      technologies: ['React', 'Next.js', 'Angular', 'ASP.net', 'C#', 'Node.js', 'Python', 'Typescript'],
      features: [
        'Mobile-First Design',
        'Lightning-Fast Loading',
        'Intuitive UI/UX',
        'Cross-Platform Harmony',
        'SEO-Ready Architecture',
        'Secure & Scalable'

      ],
      deliverables: [
        'UI/UX Design Prototypes',
        'Production-Ready Source Code',
        'Cross-Browser Compatibility',
        'Backend API & Database',
        'SEO & Analytics Setup',
        'Security Audit Report',
        'Deployment & Hosting'

      ],
      color: 'from-blue-500 to-cyan-500'
    },

    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      title: 'Data Analytics',
      shortDesc: 'Transforming complex data into actionable business insights.',
      fullDesc: 'Unlock the power of your data with advanced analytics solutions that provide actionable insights for strategic decision-making. From data warehousing to machine learning, we cover all aspects of data science.',
      technologies: ['Python', 'R', 'Tableau', 'Power BI', 'Apache Spark', 'TensorFlow'],
      features: [
        'Data Visualization',
        'Predictive Analytics',
        'Machine Learning',
        'Real-time Dashboards',
        'Data Warehousing',
        'Business Intelligence'
      ],
      deliverables: [
        'Data Strategy',
        'Analytics Platform',
        'Custom Dashboards',
        'ML Models',
        'Reporting Systems',
        'Training & Support'
      ],
      color: 'from-indigo-500 to-purple-500'
    },
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      title: 'Custom Software Development',
      shortDesc: 'Scalable custom software engineered for your specific needs.',
      fullDesc: 'Engineering bespoke software solutions that align perfectly with your business goals. We build robust, scalable, and maintainable systems utilizing clean architecture and modern engineering principles to solve complex business challenges.',
      technologies: ['Java', '.NET', 'Python', 'Go', 'Microservices', 'PostgreSQL'],
      features: [
        'Custom Architecture Design',
        'Scalable System Engineering',
        'Legacy System Modernization',
        'Enterprise Integration',
        'Robust API Development',
        'Quality-First Development'
      ],
      deliverables: [
        'Technical Blueprint',
        'Full Source Code',
        'Detailed Documentation',
        'Automated Test Suites',
        'Deployment Guide',
        'Ongoing Maintenance'
      ],
      color: 'from-cyan-500 to-blue-500'
    },
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      title: 'Mobile App Development',
      shortDesc: 'Custom mobile apps designed to convert your users.',
      fullDesc: 'Build powerful mobile applications that deliver exceptional user experiences across iOS and Android platforms. We leverage both native and cross-platform technologies to maximize reach and performance.',
      technologies: ['React Native', 'Flutter', 'Swift', 'Kotlin', 'Xamarin', 'Ionic'],
      features: [
        'Native iOS & Android Apps',
        'Cross-platform Development',
        'UI/UX Design',
        'App Store Optimization',
        'Push Notifications',
        'Offline Functionality'
      ],
      deliverables: [
        'Mobile Applications',
        'App Store Submission',
        'Backend Integration',
        'Analytics Implementation',
        'Performance Testing',
        'Ongoing Support'
      ],
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
        </svg>
      ),
      title: 'Cloud Solutions',
      shortDesc: 'Secure, scalable cloud solutions to power your growth.',
      fullDesc: 'Transform your business with robust cloud solutions that provide scalability, security, and cost-efficiency. We help you migrate, optimize, and manage your cloud infrastructure across all major platforms.',
      technologies: ['AWS', 'Azure', 'Google Cloud', 'Docker', 'Kubernetes', 'Terraform'],
      features: [
        'Cloud Migration',
        'Infrastructure as Code',
        'Auto-scaling Solutions',
        'Disaster Recovery',
        'Cost Optimization',
        'Security Implementation'
      ],
      deliverables: [
        'Cloud Architecture Design',
        'Migration Strategy',
        'DevOps Implementation',
        'Monitoring Setup',
        'Security Configuration',
        'Training & Documentation'
      ],
      color: 'from-green-500 to-teal-500'
    },
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
        </svg>
      ),
      title: 'UI/UX Design',
      shortDesc: 'Expert UI/UX design to elevate your digital experience.',
      fullDesc: 'Create intuitive and engaging user experiences that drive conversion and user satisfaction. Our design team combines aesthetic excellence with usability principles to deliver interfaces that users love.',
      technologies: ['Figma', 'Adobe XD', 'Sketch', 'InVision', 'Principle', 'Framer'],
      features: [
        'User Research',
        'Wireframing & Prototyping',
        'Visual Design',
        'Interaction Design',
        'Design Systems',
        'Usability Testing'
      ],
      deliverables: [
        'Design Strategy',
        'User Journey Maps',
        'Interactive Prototypes',
        'Design System',
        'Style Guidelines',
        'Developer Handoff'
      ],
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      ),
      title: 'DevOps & AWS Services',
      shortDesc: 'Continuous integration & deployment',
      fullDesc: 'Streamline your development lifecycle with robust DevOps practices and AWS cloud services. We implement CI/CD pipelines, infrastructure automation, and monitoring solutions that enhance productivity and reliability.',
      technologies: ['Jenkins', 'GitLab CI', 'Docker', 'Kubernetes', 'Terraform', 'AWS'],
      features: [
        'CI/CD Pipelines',
        'Infrastructure Automation',
        'Container Orchestration',
        'Monitoring & Logging',
        'Security Integration',
        'Performance Optimization'
      ],
      deliverables: [
        'DevOps Strategy',
        'Automated Pipelines',
        'Infrastructure Code',
        'Monitoring Setup',
        'Security Framework',
        'Team Training'
      ],
      color: 'from-cyan-500 to-blue-500'
    }
  ];

  return (
    <section id="services" ref={sectionRef} className="section-padding bg-slate-50">
      <div className="container-custom">
        {/* Section Header */}
        <div className={`text-center mb-16 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Our <span className="gradient-text">Services</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-cyan-400 mx-auto mb-8"></div>
          <p className="text-lg md:text-lg text-slate-600 max-w-6xl mx-auto leading-relaxed text-left">
            Scalizap delivers cutting-edge IT solutions, specializing in bespoke software development, robust cybersecurity, and scalable cloud infrastructure designed to streamline your business operations and empower sustainable growth in a digital world.
          </p>
        </div>

        {/* Services Grid - Mobile */}
        <div className="lg:hidden grid gap-6">
          {services.map((service, index) => (
            <div
              key={index}
              className={`bg-white rounded-2xl shadow-lg p-6 card-hover ${isVisible ? 'animate-fadeInUp' : 'opacity-0'
                }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center text-white mb-4`}>
                {service.icon}
              </div>
              <h3 className="font-bold text-xl text-slate-900 mb-3">{service.title}</h3>
              <p className="text-slate-600 mb-4">{service.shortDesc}</p>
              <p className="text-slate-600 text-sm mb-4 leading-relaxed">{service.fullDesc}</p>

              <div className="mb-4">
                <h4 className="font-semibold text-slate-900 mb-2">Technologies:</h4>
                <div className="flex flex-wrap gap-2">
                  {service.technologies.map((tech, techIndex) => (
                    <span key={techIndex} className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              <button className={`w-full bg-gradient-to-r ${service.color} text-white py-3 px-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105`}>
                Learn More
              </button>
            </div>
          ))}
        </div>

        {/* Services Grid - Desktop */}
        <div className="hidden lg:block">
          {/* Service Cards Grid */}
          <div className={`flex flex-wrap justify-center gap-8 mb-12 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
            {services.map((service, index) => (
              <div
                key={index}
                onClick={() => setActiveService(index)}
                className={`w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.35rem)] group cursor-pointer bg-white rounded-2xl shadow-lg p-8 transition-all duration-300 card-hover flex flex-col justify-start ${activeService === index ? 'ring-4 ring-blue-200 shadow-2xl' : ''
                  }`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  {service.icon}
                </div>
                <h3 className="font-bold text-xl text-slate-900 mb-3">{service.title}</h3>
                <p className="text-slate-600 leading-relaxed">{service.shortDesc}</p>

                {activeService === index && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <span className="text-blue-600 text-sm font-medium">Click to see details →</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Active Service Details */}
          <div className={`bg-white rounded-3xl shadow-xl p-8 md:p-12 ${isVisible ? 'animate-slideInRight' : 'opacity-0'}`} style={{ animationDelay: '0.6s' }}>
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <div className="flex items-center mb-6">
                  <div className={`w-20 h-20 bg-gradient-to-br ${services[activeService].color} rounded-2xl flex items-center justify-center text-white mr-6`}>
                    {services[activeService].icon}
                  </div>
                  <div>
                    <h3 className="font-display text-3xl font-bold text-slate-900 mb-2">
                      {services[activeService].title}
                    </h3>
                    <p className="text-slate-600 font-medium text-left">{services[activeService].shortDesc}</p>
                  </div>
                </div>

                <p className="text-slate-600 leading-relaxed mb-8 text-left">
                  {services[activeService].fullDesc}
                </p>

                <div className="flex gap-4">
                  <button className={`bg-gradient-to-r ${services[activeService].color} text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-lg`}>
                    Get Started
                  </button>
                  <button className="border-2 border-slate-200 text-slate-700 px-8 py-3 rounded-xl font-semibold transition-all duration-300 hover:bg-slate-50">
                    Learn More
                  </button>
                </div>
              </div>

              <div className="space-y-8">
                <div>
                  <h4 className="font-bold text-slate-900 mb-4">Key Features</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {services[activeService].features.map((feature, index) => (
                      <div key={index} className="flex items-center text-slate-600">
                        <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-bold text-slate-900 mb-4">Technologies</h4>
                  <div className="flex flex-wrap gap-3">
                    {services[activeService].technologies.map((tech, index) => (
                      <span key={index} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-bold text-slate-900 mb-4">Deliverables</h4>
                  <div className="space-y-2">
                    {services[activeService].deliverables.map((deliverable, index) => (
                      <div key={index} className="flex items-center text-slate-600">
                        <svg className="w-4 h-4 text-blue-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <span className="text-sm">{deliverable}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Process Section */}
        <div className={`mt-20 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '0.8s' }}>
          <div className="text-center mb-12">
            <h3 className="font-display text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Our Proven Process
            </h3>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              A systematic approach that ensures successful project delivery every time
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discovery & Planning', desc: 'Understanding requirements and creating detailed project roadmap' },
              { step: '02', title: 'Design & Development', desc: 'Creating user-centric designs and building robust solutions' },
              { step: '03', title: 'Testing & Quality Assurance', desc: 'Comprehensive testing to ensure reliability and performance' },
              { step: '04', title: 'Deployment & Support', desc: 'Seamless launch and ongoing maintenance and support' }
            ].map((process, index) => (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center text-white font-bold text-xl mb-4 mx-auto group-hover:scale-110 transition-transform duration-300">
                  {process.step}
                </div>
                <h4 className="font-semibold text-slate-900 mb-2">{process.title}</h4>
                <p className="text-slate-600 text-sm leading-relaxed">{process.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;